import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTheme } from "./ThemeProvider";
import { Search, ArrowRight, Star } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface CommandItem {
  id: string;
  label: string;
  path: string;
  category: string;
  keywords: string[];
  isBossStage?: boolean;
}

const allItems = [
  { name: "Overview", path: "/", keywords: ["home", "start", "introduction"] },
  { name: "Brand & Logos", path: "/brand", keywords: ["identity", "logo", "branding", "guidelines"] },
  { name: "Color System", path: "/colors", keywords: ["palette", "purple", "blue", "orange", "colors"] },
  { name: "Typography", path: "/typography", keywords: ["fonts", "type", "text", "space grotesk", "inter"] },
  { name: "Components", path: "/components", keywords: ["ui", "buttons", "cards", "inputs"] },
  { name: "Grid System", path: "/grid", keywords: ["layout", "columns", "responsive", "breakpoints"] },
  { name: "Responsive System", path: "/responsive", keywords: ["responsive", "breakpoints", "tiers", "mobile", "tablet", "4k", "ultra-wide", "screen", "dual qhd", "8k", "simulator", "device", "css variables", "exp-", "type-scale", "container", "grid", "spacing", "visibility", "glow"] },
  { name: "Interface", path: "/navigation", keywords: ["nav", "menu", "sidebar", "navigation"] },
  { name: "Metrics", path: "/dashboard", keywords: ["dashboard", "charts", "analytics", "data"] },
  { name: "Motion & Fluency", path: "/motion", keywords: ["animation", "transitions", "motion"] },
  { name: "Gamification", path: "/gamification", keywords: ["levels", "progression", "xp", "achievements"] },
  { name: "Design Tokens", path: "/tokens", keywords: ["css", "variables", "tokens", "values"] },
];

const commands: CommandItem[] = allItems.map((item, index) => ({
  id: index.toString(),
  label: item.name,
  path: item.path,
  category: "Pages",
  keywords: item.keywords,
  isBossStage: item.name === "Brand & Logos" || item.name === "Color System" || item.name === "Components" || item.name === "Gamification",
}));

export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  // Expose open function to parent
  useEffect(() => {
    (window as any).openCommandPalette = () => setIsOpen(true);
    return () => {
      delete (window as any).openCommandPalette;
    };
  }, []);

  // Fuzzy search implementation
  const filteredCommands = useMemo(() => {
    if (!query) return commands;
    
    const searchTerm = query.toLowerCase();
    return commands.filter(cmd => {
      const labelMatch = cmd.label.toLowerCase().includes(searchTerm);
      const keywordMatch = cmd.keywords.some(kw => kw.includes(searchTerm));
      return labelMatch || keywordMatch;
    });
  }, [query]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ⌘K or Ctrl+K to toggle
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(prev => !prev);
        setQuery("");
        setSelectedIndex(0);
      }

      if (!isOpen) return;

      // Escape to close
      if (e.key === "Escape") {
        setIsOpen(false);
        setQuery("");
        setSelectedIndex(0);
      }

      // Arrow navigation
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      }

      // Enter to navigate
      if (e.key === "Enter" && filteredCommands[selectedIndex]) {
        navigate({ to: filteredCommands[selectedIndex].path as string });
        setIsOpen(false);
        setQuery("");
        setSelectedIndex(0);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands, navigate]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-50"
            onClick={() => setIsOpen(false)}
          />

          {/* Command Palette */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2, type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-[15vh] left-1/2 -translate-x-1/2 w-full max-w-[600px] z-50 px-4"
          >
            <div className={`rounded-2xl overflow-hidden shadow-2xl border ${
              isDark 
                ? "glass-thick border-white/10" 
                : "bg-white/95 backdrop-blur-xl border-border shadow-xl"
            }`}>
              {/* Search Input */}
              <div className={`flex items-center gap-3 px-4 py-4 border-b ${
                isDark ? "border-white/5" : "border-border"
              }`}>
                <Search size={20} className={isDark ? "text-muted-foreground" : "text-muted-foreground"} />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search pages, components, tokens..."
                  className={`flex-1 bg-transparent outline-none text-base placeholder:text-muted-foreground ${
                    isDark ? "text-white" : "text-muted-foreground"
                  }`}
                  autoFocus
                />
                <kbd className={`px-2 py-1 rounded exp-small tracking-wider ${
                  isDark ? "bg-white/5 text-muted-foreground" : "bg-muted text-muted-foreground"
                }`}>ESC</kbd>
              </div>

              {/* Results */}
              <div className="max-h-[400px] overflow-y-auto">
                {filteredCommands.length === 0 ? (
                  <div className={`px-4 py-8 text-center text-sm ${
                    isDark ? "text-muted-foreground" : "text-muted-foreground"
                  }`}>
                    No results found
                  </div>
                ) : (
                  <div className="py-2">
                    {filteredCommands.map((cmd, idx) => (
                      <button
                        key={cmd.id}
                        onClick={() => {
                          navigate({ to: cmd.path as string });
                          setIsOpen(false);
                          setQuery("");
                        }}
                        className={`w-full px-4 py-3 flex items-center justify-between gap-3 transition-colors ${
                          idx === selectedIndex
                            ? isDark
                              ? "bg-primary/10"
                              : "bg-primary/5"
                            : isDark
                            ? "hover:bg-white/5"
                            : "hover:bg-muted"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {cmd.isBossStage && (
                            <Star size={14} className="text-[var(--p-legendary)]" fill="var(--p-legendary)" />
                          )}
                          <span className={`text-sm ${
                            isDark ? "text-white" : "text-muted-foreground"
                          }`}>
                            {cmd.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`exp-small ${
                            isDark ? "text-muted-foreground" : "text-muted-foreground"
                          }`}>
                            {cmd.category}
                          </span>
                          <ArrowRight size={14} className={isDark ? "text-muted-foreground" : "text-muted-foreground"} />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className={`px-4 py-3 border-t flex items-center justify-between exp-small ${
                isDark ? "border-white/5 text-muted-foreground" : "border-border text-muted-foreground"
              }`}>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <kbd className={`px-1.5 py-0.5 rounded ${isDark ? "bg-white/5" : "bg-muted"}`}>↑</kbd>
                    <kbd className={`px-1.5 py-0.5 rounded ${isDark ? "bg-white/5" : "bg-muted"}`}>↓</kbd>
                    <span className="ml-1">navigate</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <kbd className={`px-1.5 py-0.5 rounded ${isDark ? "bg-white/5" : "bg-muted"}`}>↵</kbd>
                    <span className="ml-1">select</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Star size={10} className="text-[var(--p-legendary)]" fill="var(--p-legendary)" />
                  <span>Boss Stage</span>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}